import { apiDelete, apiGet, apiPost } from '@/services/api';
import type { TerritorialUnit } from '@/features/common/types/TerritorialUnit';
import type { EventDetail } from '@/features/publicsite/types/events/EventDetail';
import type { EventFeedbackPayload } from '@/features/publicsite/types/events/EventFeedbackPayload';
import type { EventFeedbackSummary } from '@/features/publicsite/types/events/EventFeedbackSummary';
import type { EventFilters } from '@/features/publicsite/types/events/EventFilters';
import type { EventItem } from '@/features/publicsite/types/events/EventItem';
import type { EventOrder } from '@/features/publicsite/types/events/EventOrder';
import type { EventPageResponse } from '@/features/publicsite/types/events/EventPageResponse';
import type { EventParticipation } from '@/features/publicsite/types/events/EventParticipation';
import type { OrganizerOption } from '@/features/publicsite/types/events/OrganizerOption';

const PUBLIC_EVENTS = '/api/v1/public/events';
const PUBLIC_EVENT_ORGANIZERS = '/api/v1/public/event-organizers';
const EVENTS = '/api/v1/events';

/** How many events one page of /evenimente holds — the endpoint's own default, said out loud. */
export const EVENT_PAGE_SIZE = 12;

/**
 * The calendar request for a set of cleaned filters (eventFilters).
 *
 * A cleared filter is left out of the query rather than sent empty — this endpoint is publicly
 * cached, and `?region=&theme=` is a different URL from no query at all for the same answer, so
 * sending the blanks would split the cache across every combination of them.
 */
export function eventsQuery(filters: EventFilters): string {
  const qs = new URLSearchParams(filters).toString();
  return `${PUBLIC_EVENTS}${qs ? `?${qs}` : ''}`;
}

/**
 * One page of the calendar, for a set of cleaned filters.
 *
 * `page` counts from 1 here, as the URL and the pager do, and is sent counted from 0, as the
 * endpoint reads it. `order` is always sent, never left to the default: the endpoint's default
 * depends on the shape it answers in (`desc` for a page, `asc` for the bare array), so an omitted
 * order is a direction decided somewhere else.
 *
 * The blanks are still left out (see eventsQuery), but `page`, `size` and `order` always go: they
 * are what makes this a page rather than the whole calendar.
 */
export function eventsPageQuery(
  filters: EventFilters,
  page: number,
  order: EventOrder,
  size = EVENT_PAGE_SIZE,
): string {
  const qs = new URLSearchParams(filters);
  qs.set('page', String(page - 1));
  qs.set('size', String(size));
  qs.set('order', order);
  return `${PUBLIC_EVENTS}?${qs}`;
}

/** Every request /evenimente and an event's own page make — they and their hooks call these. */
export const publicEventsApi = {
  /** One calendar request, as eventsQuery wrote it. */
  list: (request: string) => apiGet<EventItem[]>(request),
  /** One page of the calendar, as eventsPageQuery wrote it. */
  page: (request: string) => apiGet<EventPageResponse>(request),
  /** The "Organizator" list: every organiser with a public event, named and sorted by the server. */
  organizers: () => apiGet<OrganizerOption[]>(PUBLIC_EVENT_ORGANIZERS),
  /** The raions the "Raion" list offers. */
  territorialUnits: () => apiGet<TerritorialUnit[]>('/api/v1/public/territorial-units'),
  /** The slug for every link the site hands out; a bare id resolves too. */
  detail: (idOrSlug: string) => apiGet<EventDetail>(`${PUBLIC_EVENTS}/${idOrSlug}`),
  feedbackSummary: (idOrSlug: string) =>
    apiGet<EventFeedbackSummary>(`${PUBLIC_EVENTS}/${idOrSlug}/feedback/summary`),
  /** The signed-in visitor's own standing with the event — the one call that needs a session. */
  participation: (eventId: string) => apiGet<EventParticipation>(`${EVENTS}/${eventId}/me`),
  /** `{}`: the registration carries nothing but the session it is made in. */
  register: (eventId: string) => apiPost(`${EVENTS}/${eventId}/registrations`, {}),
  /** Withdraws the visitor's own registration, until the event starts. */
  cancelRegistration: (eventId: string) => apiDelete(`${EVENTS}/${eventId}/registrations/me`),
  leaveFeedback: (eventId: string, body: EventFeedbackPayload) =>
    apiPost(`${EVENTS}/${eventId}/feedback`, body),
};
